<script setup lang="ts">
  import { computed, provide, useId, useSlots } from 'vue';
  import { attributeContextKey } from '../Attribute/Attribute.context';
  import {
    LegacyAttributeListGap,
    type LegacyAttributeListProps,
    type LegacyAttributeListSlots,
    LegacyAttributeListSurface,
    LegacyAttributeListVariant,
  } from './LegacyAttributeList.types';
  import {
    actionClass,
    expandClass,
    hasActionClass,
    rootClass,
    surfaceClass,
    twoColumnsClass,
    twoColumnsGapClasses,
    variantClass,
    withDividersClass,
  } from './LegacyAttributeList.css';

  const props = withDefaults(defineProps<LegacyAttributeListProps>(), {
    withDividers: false,
    expand: false,
    columns: 1,
    gap: LegacyAttributeListGap.Wide,
    variant: LegacyAttributeListVariant.Light,
    surface: LegacyAttributeListSurface.Base,
  });

  defineSlots<LegacyAttributeListSlots>();

  provide(attributeContextKey, null);

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-attribute-list-${uid}`,
  );

  const slots = useSlots();
  const hasAction = computed(() => !!slots.action);

  const surfaceClasses = computed(() =>
    props.variant === LegacyAttributeListVariant.Prominent
      ? surfaceClass[props.surface]
      : undefined,
  );

  const rootClasses = computed(() =>
    props.columns === 2
      ? [
          twoColumnsClass,
          twoColumnsGapClasses[props.gap],
          variantClass[props.variant],
          surfaceClasses.value,
          { [hasActionClass]: hasAction.value },
        ]
      : [
          rootClass,
          variantClass[props.variant],
          surfaceClasses.value,
          {
            [withDividersClass]: props.withDividers,
            [expandClass]: props.expand,
            [hasActionClass]: hasAction.value,
          },
        ],
  );
</script>

<template>
  <div :class="rootClasses" :data-testid="resolvedTestId">
    <slot />
    <div
      v-if="hasAction"
      :class="actionClass"
      :data-testid="`${resolvedTestId}-action`"
    >
      <slot name="action" />
    </div>
  </div>
</template>
